import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Info, SendHorizontal } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";

const CATEGORIES = [
  "Air connectivity",
  "Trade and investment",
  "Health-worker cooperation",
  "Education",
  "Culture and creative industries",
  "Reparatory justice",
];

const COLUMNS = [
  "Commitment",
  "Announced by",
  "Announcement date",
  "Target date",
  "Current status",
  "Evidence",
  "Outstanding question",
  "Last verified",
];

const STATUS_STYLES = {
  Announced: "border-atlantic/30 text-atlantic",
  Planning: "border-ocean/50 text-ocean",
  "In progress": "border-gold text-gold",
  Completed: "border-emerald-700/50 text-emerald-800",
  Delayed: "border-red-800/40 text-red-800",
  Unverified: "border-slate-300 text-slate-400",
};

const ROWS = [
  {
    "category": "Air connectivity",
    "commitment": "Explore a direct Ghana–Jamaica air link and closer aviation cooperation; no operating service evidenced in the cited announcement.",
    "announcedBy": "Ghana Ministry of Foreign Affairs",
    "announced": "3 August 2026",
    "target": "No launch date announced",
    "status": "Planning",
    "evidence": "Ghana MFA report of Mahama–Holness bilateral talks",
    "evidenceUrl": "https://mfa.gov.gh/index.php/as-part-of-his-historic-state-visit-to-jamaica-president-john-dramani-mahama-held-bilateral-talks-on-monday-3rd-august-2026-with-the-most-honourable-dr-andrew-holness-prime-minister-of-jamaica/",
    "question": "Has a feasibility study, operator, financing plan or route timetable been published?",
    "verified": "15 October 2026"
  },
  {
    "category": "Trade and investment",
    "commitment": "Pursue bilateral trade and investment; Jamaica announced plans for an export and business mission to Ghana.",
    "announcedBy": "Jamaica Ministry of Foreign Affairs and Foreign Trade",
    "announced": "3 June 2026",
    "target": "Mission proposed for July 2026; completion not verified",
    "status": "Announced",
    "evidence": "Jamaican MFAFT release on the Third Joint Commission",
    "evidenceUrl": "https://mfaft.gov.jm/site/jamaica-ghana-set-new-course-for-cooperation-after-two-decadesand/",
    "question": "Did the proposed mission take place, and what transactions or follow-up commitments resulted?",
    "verified": "15 October 2026"
  },
  {
    "category": "Health-worker cooperation",
    "commitment": "Four-year health workforce mobility agreement; 43 Ghanaian specialist nurses arrived in late September and have since been deployed across Jamaica's health regions. Programme target: 400 workers.",
    "announcedBy": "Jamaica Ministry of Health & Wellness",
    "announced": "26 May 2026 agreement; late-September arrival",
    "target": "Four-year agreement; further deployment dates not verified",
    "status": "In progress",
    "evidence": "Jamaica Information Service update on deployment across health regions (12 October)",
    "evidenceUrl": "https://jis.gov.jm/govt-expanding-recruitment-of-specialist-healthcare-professionals/",
    "question": "What are the next deployment dates, contract safeguards and measures to protect Ghanaian workforce capacity?",
    "verified": "15 October 2026"
  },
  {
    "category": "Education",
    "commitment": "Pursue education and academic exchanges under the renewed Joint Commission agenda.",
    "announcedBy": "Ghana and Jamaica foreign ministries",
    "announced": "26 May 2026",
    "target": "No programme deadline identified",
    "status": "Announced",
    "evidence": "Ghana MFA account of bilateral cooperation priorities",
    "evidenceUrl": "https://mfa.gov.gh/index.php/ministerial-session-of-the-third-permanent-joint-commission-for-cooperation-between-ghana-and-jamaica/",
    "question": "Which universities, programmes, funding lines and student exchanges have been formally agreed?",
    "verified": "15 October 2026"
  },
  {
    "category": "Culture and creative industries",
    "commitment": "Signed exchange of notes amending the existing bilateral cooperation agreement in arts and culture.",
    "announcedBy": "Ghana Ministry of Foreign Affairs",
    "announced": "26 May 2026",
    "target": "No implementation date announced",
    "status": "Announced",
    "evidence": "Signed instruments listed by Ghana MFA",
    "evidenceUrl": "https://mfa.gov.gh/index.php/ministerial-session-of-the-third-permanent-joint-commission-for-cooperation-between-ghana-and-jamaica/",
    "question": "What funded cultural projects, rights protections or exchanges have followed?",
    "verified": "15 October 2026"
  },
  {
    "category": "Reparatory justice",
    "commitment": "Diplomatic advocacy and dialogue on reparatory justice; no separate bilateral implementation instrument identified in the cited Joint Commission list.",
    "announcedBy": "Ghana Presidency and foreign ministry",
    "announced": "May–August 2026",
    "target": "No bilateral project deadline announced",
    "status": "Announced",
    "evidence": "Ghana Presidency programme for Jamaica visit",
    "evidenceUrl": "https://presidency.gov.gh/statement-president-mahama-to-undertake-historic-official-visit-to-jamaica/",
    "question": "Will dialogue produce a published joint research, archival, restitution or policy work programme?",
    "verified": "15 October 2026"
  }
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "");

const StatusPill = ({ status }) => (
  <span className={`inline-block border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${STATUS_STYLES[status]}`}>
    {status}
  </span>
);

export default function TrackerPage() {
  const [filter, setFilter] = useState("All");
  const rows = filter === "All" ? ROWS : ROWS.filter((r) => r.category === filter);

  const submit = () =>
    toast.info("Please use the contact form to submit source documents or corrections.", {
      duration: 5000,
    });

  return (
    <main data-testid="tracker-page">
      <div className="border-b border-atlantic/10 bg-sand-dark">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          <Link to="/research" data-testid="tracker-back-research" className="group flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-atlantic/70 transition-colors hover:text-ocean">
            <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" /> Research
          </Link>
          <Link to="/countries/jamaica" data-testid="tracker-dossier-link" className="text-xs font-semibold uppercase tracking-[0.2em] text-atlantic/70 transition-colors hover:text-ocean">
            Jamaica Dossier
          </Link>
        </div>
      </div>

      <PageHero
        kicker="Institutional Tracker"
        title="Ghana–Jamaica Cooperation Tracker"
        copy="A public-interest monitoring tool tracking announced commitments, implementation evidence and unresolved questions in Ghana–Jamaica cooperation."
      />

      <section className="border-b border-atlantic/10 bg-sand-dark py-10">
        <div className="mx-auto flex max-w-7xl gap-4 px-5 lg:px-8">
          <Info size={18} className="mt-1 shrink-0 text-gold" />
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-atlantic">Methodology</p>
            <p className="mt-2 max-w-4xl text-sm leading-relaxed text-slate-600">
              Entries are added to this tracker only after documentary or attributable verification — official
              statements, published agreements, budget lines, parliamentary records or on-the-record confirmation.
              Every entry carries a last-verified date. “Announced” denotes an attributable statement or agreement, “Planning” denotes development without a verified service,
              and “In progress” denotes an evidenced implementation step; none by itself proves a programme is complete.
              Dates and evidence links document what was verified, not what may have happened afterwards.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-sand py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap items-center gap-3" data-testid="tracker-filters" role="group" aria-label="Filter by cooperation area">
              {["All", ...CATEGORIES].map((c) => (
                <button
                  key={c}
                  data-testid={`tracker-filter-${slug(c)}`}
                  onClick={() => setFilter(c)}
                  aria-pressed={filter === c}
                  className={`border px-4 py-2.5 text-xs font-semibold tracking-wide transition-colors ${
                    filter === c
                      ? "border-atlantic bg-atlantic text-white"
                      : "border-atlantic/15 bg-white text-atlantic/70 hover:border-ocean hover:text-ocean"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="mt-10 hidden lg:block">
            <div className="overflow-x-auto border border-atlantic/10 bg-white">
              <table className="w-full min-w-[1200px] border-collapse text-left" data-testid="tracker-table">
                <thead>
                  <tr className="border-b border-atlantic/10 bg-sand-dark">
                    <th className="px-5 py-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Area</th>
                    {COLUMNS.map((c) => (
                      <th key={c} className="px-5 py-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.category} data-testid={`tracker-row-${slug(r.category)}`} className="border-b border-atlantic/5 last:border-0">
                      <td className="px-5 py-5 font-serif text-base text-atlantic">{r.category}</td>
                      <td className="px-5 py-5 text-sm leading-relaxed text-slate-700">{r.commitment}</td>
                      <td className="px-5 py-5 text-sm leading-relaxed text-slate-700">{r.announcedBy}</td>
                      <td className="px-5 py-5 text-sm leading-relaxed text-slate-700">{r.announced}</td>
                      <td className="px-5 py-5 text-sm leading-relaxed text-slate-700">{r.target}</td>
                      <td className="px-5 py-5"><StatusPill status={r.status} /></td>
                      <td className="px-5 py-5 text-sm leading-relaxed text-slate-700"><a className="font-medium text-ocean underline underline-offset-2" href={r.evidenceUrl} target="_blank" rel="noopener noreferrer">{r.evidence} ↗</a></td>
                      <td className="px-5 py-5 text-sm leading-relaxed text-slate-700">{r.question}</td>
                      <td className="px-5 py-5 text-sm leading-relaxed text-slate-700">{r.verified}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          <div className="mt-10 space-y-5 lg:hidden" data-testid="tracker-cards">
            {rows.map((r) => (
              <Reveal key={r.category}>
                <article data-testid={`tracker-card-${slug(r.category)}`} className="border border-atlantic/10 bg-white p-6">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-serif text-lg text-atlantic">{r.category}</h3>
                    <StatusPill status={r.status} />
                  </div>
                  <dl className="mt-5 space-y-3">
                    {[
                      ["Commitment", r.commitment],
                      ["Announced by", r.announcedBy],
                      ["Announcement date", r.announced],
                      ["Target date", r.target],
                      ["Evidence", r.evidence],
                      ["Outstanding question", r.question],
                      ["Last verified", r.verified],
                    ].map(([k, v]) => (
                      <div key={k} className="flex flex-col gap-0.5 border-b border-atlantic/5 pb-3 last:border-0">
                        <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{k}</dt>
                        <dd className="text-sm leading-relaxed text-slate-700">{k === "Evidence" ? <a className="font-medium text-ocean underline underline-offset-2" href={r.evidenceUrl} target="_blank" rel="noopener noreferrer">{v} ↗</a> : v}</dd>
                      </div>
                    ))}
                  </dl>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15} className="mt-12 border border-gold/40 bg-sand-dark p-7 sm:p-9">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="max-w-xl">
                <p className="font-serif text-xl text-atlantic sm:text-2xl">Hold the record straight</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Documentary evidence, corrections and official confirmations are reviewed by the editorial desk
                  before any entry is added or amended.
                </p>
              </div>
              <button
                data-testid="tracker-submit-evidence-button"
                onClick={submit}
                className="group inline-flex shrink-0 items-center gap-2 bg-atlantic px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-atlantic-light"
              >
                <SendHorizontal size={15} />
                Submit Evidence or Correction
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
